import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ClipboardCheck, Search, FileDown, ExternalLink, Camera } from 'lucide-react';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

export default function HistoriqueInspections() {
  const [filters, setFilters] = useState({
    equipement_id: '',
    client_nom: '',
    type_inspection: 'all',
    conforme: 'all',
    date_debut: '',
    date_fin: '',
  });
  const [data, setData] = useState({ inspections: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [equipements, setEquipements] = useState([]);
  const [selected, setSelected] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.equipement_id) params.equipement_id = filters.equipement_id;
      if (filters.client_nom.trim()) params.client_nom = filters.client_nom.trim();
      if (filters.type_inspection !== 'all') params.type_inspection = filters.type_inspection;
      if (filters.conforme !== 'all') params.conforme = filters.conforme === 'oui';
      if (filters.date_debut) params.date_debut = filters.date_debut;
      if (filters.date_fin) params.date_fin = filters.date_fin;
      const res = await axios.get(`${API_URL}/api/inspections-historique`, { params });
      setData(res.data || { inspections: [], stats: {} });
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => {
    axios.get(`${API_URL}/api/equipements-inventaire`)
      .then(res => setEquipements(res.data?.equipements || []))
      .catch(() => {});
  }, []);

  useEffect(() => { reload(); }, []); // eslint-disable-line

  const exporter = () => {
    const rows = data.inspections.map(i => ({
      Date: new Date(i.date_inspection).toLocaleDateString('fr-CA'),
      Equipement: i.equipement_nom,
      Type: i.type_inspection === 'pre_location' ? 'AVANT location' : i.type_inspection === 'post_location' ? 'APRÈS location' : 'Routine',
      Inspecteur: i.inspecteur_nom,
      Client: i.client_nom || '',
      Conforme: i.conforme ? 'Oui' : 'Non',
      'Nb problèmes': (i.items || []).filter(it => !it.checked).length,
      'Nb photos': (i.photos || []).length,
      Notes: (i.notes_generales || '').replace(/\n/g, ' '),
    }));
    if (rows.length === 0) return;
    const headers = Object.keys(rows[0]);
    const csv = [
      headers.join(';'),
      ...rows.map(r => headers.map(h => `"${(r[h] || '').toString().replace(/"/g, '""')}"`).join(';')),
    ].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inspections_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const stats = data.stats || {};
  const inspections = data.inspections || [];
  const equipementsMap = useMemo(() => Object.fromEntries(equipements.map(e => [e.id, e])), [equipements]);

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-6xl mx-auto" data-testid="page-historique-inspections">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
          <ClipboardCheck className="w-6 h-6 text-amber-600" />
          Historique des inspections
        </h1>
        <Button variant="outline" onClick={exporter} disabled={inspections.length === 0} data-testid="btn-export-csv">
          <FileDown className="w-4 h-4 mr-2" /> Exporter CSV
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card><CardContent className="pt-4 pb-3"><p className="text-xs text-slate-500">Total</p><p className="text-2xl font-bold">{stats.total || 0}</p></CardContent></Card>
        <Card className="border-emerald-200"><CardContent className="pt-4 pb-3"><p className="text-xs text-emerald-700">Conformes</p><p className="text-2xl font-bold text-emerald-700">{stats.nb_conformes || 0}</p></CardContent></Card>
        <Card className="border-red-200"><CardContent className="pt-4 pb-3"><p className="text-xs text-red-700">Non conformes</p><p className="text-2xl font-bold text-red-700">{stats.nb_non_conformes || 0}</p></CardContent></Card>
        <Card><CardContent className="pt-4 pb-3"><p className="text-xs text-slate-500">Avec photos</p><p className="text-2xl font-bold">{stats.nb_avec_photos || 0}</p></CardContent></Card>
      </div>

      {/* Filtres */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Filtres</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            <Select value={filters.equipement_id || 'all'} onValueChange={v => setFilters({ ...filters, equipement_id: v === 'all' ? '' : v })}>
              <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Toutes nacelles" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les nacelles</SelectItem>
                {equipements.map(e => <SelectItem key={e.id} value={e.id}>{e.nom} {e.numero_unite ? `(#${e.numero_unite})` : ''}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input placeholder="Client (nom contient...)" value={filters.client_nom}
              onChange={e => setFilters({ ...filters, client_nom: e.target.value })} className="h-9 text-sm" data-testid="filter-client" />
            <Select value={filters.type_inspection} onValueChange={v => setFilters({ ...filters, type_inspection: v })}>
              <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous types</SelectItem>
                <SelectItem value="pre_location">📥 AVANT location</SelectItem>
                <SelectItem value="post_location">📤 APRÈS location</SelectItem>
                <SelectItem value="routine">🔧 Routine</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.conforme} onValueChange={v => setFilters({ ...filters, conforme: v })}>
              <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Conformes + non conformes</SelectItem>
                <SelectItem value="oui">✅ Conformes seulement</SelectItem>
                <SelectItem value="non">⚠️ Non conformes seulement</SelectItem>
              </SelectContent>
            </Select>
            <Input type="date" value={filters.date_debut} onChange={e => setFilters({ ...filters, date_debut: e.target.value })} className="h-9 text-sm" />
            <Input type="date" value={filters.date_fin} onChange={e => setFilters({ ...filters, date_fin: e.target.value })} className="h-9 text-sm" />
          </div>
          <div className="flex gap-2 justify-end pt-1">
            <Button variant="outline" size="sm" onClick={() => { setFilters({ equipement_id: '', client_nom: '', type_inspection: 'all', conforme: 'all', date_debut: '', date_fin: '' }); setTimeout(reload, 50); }}>
              Réinitialiser
            </Button>
            <Button size="sm" onClick={reload} data-testid="btn-appliquer-filtres" className="bg-amber-600 hover:bg-amber-700 text-white">
              <Search className="w-3 h-3 mr-1" /> Appliquer
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Liste */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-10 text-slate-400">Chargement…</div>
          ) : inspections.length === 0 ? (
            <div className="text-center py-10 text-slate-400">Aucune inspection ne correspond aux filtres.</div>
          ) : (
            <div className="divide-y">
              {inspections.map(ins => {
                const probs = (ins.items || []).filter(i => !i.checked).length;
                const eq = equipementsMap[ins.equipement_id];
                return (
                  <div key={ins.id} className="p-3 hover:bg-slate-50 cursor-pointer" onClick={() => setSelected(ins)} data-testid={`row-inspection-${ins.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold flex items-center gap-2">
                          {ins.conforme ? '✅' : '⚠️'}
                          <span className="truncate">{ins.equipement_nom || eq?.nom || 'Nacelle'}</span>
                          {eq?.numero_unite && <span className="text-slate-400 font-normal">#{eq.numero_unite}</span>}
                        </p>
                        <p className="text-xs text-slate-600 mt-0.5">
                          <span className="bg-slate-100 px-1.5 py-0.5 rounded mr-2">
                            {ins.type_inspection === 'pre_location' ? '📥 AVANT' : ins.type_inspection === 'post_location' ? '📤 APRÈS' : '🔧 Routine'}
                          </span>
                          Inspecteur : <strong>{ins.inspecteur_nom || '—'}</strong>
                          {ins.client_nom && <> · Client : <strong>{ins.client_nom}</strong></>}
                        </p>
                        <p className="text-[11px] text-slate-400 mt-0.5">
                          {new Date(ins.date_inspection).toLocaleString('fr-CA')}
                          {probs > 0 && <span className="ml-2 text-red-600 font-semibold">{probs} problème{probs > 1 ? 's' : ''}</span>}
                          {(ins.photos || []).length > 0 && <span className="ml-2"><Camera className="w-3 h-3 inline" /> {ins.photos.length}</span>}
                        </p>
                      </div>
                      <ExternalLink className="w-4 h-4 text-slate-300 mt-1 shrink-0" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Détail (modale) */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between">
              <h3 className="text-base font-bold">
                {selected.conforme ? '✅' : '⚠️'} {selected.equipement_nom} — {new Date(selected.date_inspection).toLocaleDateString('fr-CA')}
              </h3>
              <button onClick={() => setSelected(null)} className="text-slate-400 text-2xl">×</button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <p><strong>Inspecteur :</strong> {selected.inspecteur_nom}</p>
              {selected.client_nom && <p><strong>Client :</strong> {selected.client_nom}</p>}
              <p><strong>Type :</strong> {selected.type_inspection}</p>
              <div>
                <p className="font-semibold mb-1">Items :</p>
                <ul className="space-y-0.5">
                  {(selected.items || []).map((it, i) => (
                    <li key={i} className={`text-xs ${it.checked ? 'text-emerald-700' : 'text-red-700 font-semibold'}`}>
                      {it.checked ? '✅' : '⚠️'} {it.label}{it.notes ? ` — ${it.notes}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
              {selected.notes_generales && <p><strong>Notes :</strong> {selected.notes_generales}</p>}
              {(selected.photos || []).length > 0 && (
                <div>
                  <p className="font-semibold mb-1">Photos ({selected.photos.length}) :</p>
                  <div className="flex flex-wrap gap-2">
                    {selected.photos.map((p, i) => (
                      <a key={i} href={p.startsWith('http') ? p : `${API_URL}${p}`} target="_blank" rel="noreferrer">
                        <img src={p.startsWith('http') ? p : `${API_URL}${p}`} alt="" className="w-24 h-24 object-cover rounded border hover:ring-2 hover:ring-blue-400" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
